import { useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { turbinesKey } from '../../../solaris-lens-common-lib/src/constants/queryKeys/Turbines';
import {
  getTurbinesHistoryAuditingReport,
  getTurbinesHistoryAuditingReportDownload
} from '../../../solaris-lens-common-lib/src/services/installedProducts';
import { RootState } from '../../../store';
import useShouldRefresh from '@/hooks/useShouldRefresh';

export interface AuditReportTurbineRequest {
  id: string;
  name?: string;
  subType?: string;
}

export interface AuditReportLocationRequest {
  id: string;
  name: string;
  city?: string;
  state?: string;
  timezone: string;
  turbines: AuditReportTurbineRequest[];
}

export interface AuditReportRequest {
  startDate: string;
  endDate: string;
  locations: AuditReportLocationRequest[];
  filters: string[];
}

export interface AuditReportRecord {
  date: string;
  fuelData?: number;
  engineFiredHoursAnalog?: number;
  monthlyStarts?: number;
  monthlyStops?: number;
}

export interface AuditReportResponseTurbine {
  id: string;
  name?: string;
  subType?: string;
  dataRecords: AuditReportRecord[];
}

export interface AuditReportResponseLocation {
  id: string;
  name: string;
  city?: string;
  state?: string;
  timezone: string;
  turbines: AuditReportResponseTurbine[];
}

export interface AuditReportData {
  locations?: AuditReportResponseLocation[];
}

export interface AuditReportResponse {
  data: AuditReportData;
  meta?: unknown;
}

export type AuditReportFormat = 'csv' | 'json';

interface GetAuditingReportOptions {
  applyDateFormat?: boolean;
  useQueryOptions?: object;
}

export const useGetAuditingReport = (
  request: AuditReportRequest | null,
  {
    applyDateFormat = true,
    useQueryOptions = {}
  }: GetAuditingReportOptions = {}
) => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);
  useShouldRefresh();

  return useQuery<AuditReportResponse>({
    queryKey: [...turbinesKey.auditingReport(token, request), applyDateFormat],
    queryFn: () =>
      getTurbinesHistoryAuditingReport({
        token,
        params: { applyDateFormat },
        body: request
      }) as Promise<AuditReportResponse>,
    enabled: Boolean(token && request && request.locations.length > 0),
    meta: { errorDisplay: 'toast' as const },
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...useQueryOptions
  });
};

export const useDownloadAuditingReport = () => {
  const token = useSelector((state: RootState) => state?.auth?.accessToken);

  return useMutation({
    mutationFn: async ({
      request,
      format = 'csv',
      applyDateFormat = true
    }: {
      request: AuditReportRequest;
      format?: AuditReportFormat;
      applyDateFormat?: boolean;
    }) => {
      const response = await getTurbinesHistoryAuditingReportDownload({
        token,
        params: { applyDateFormat, format },
        body: request
      });
      return response.data as Blob;
    }
  });
};
